//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
//
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ToolResultBlock } from '../../../../types/streaming';

interface BlocksRendererProps {
  blocks?: ToolResultBlock[];
  isError?: boolean;
}

const toText = (r: unknown): string => {
  if (typeof r === 'string') return r;
  try {
    return JSON.stringify(r, null, 2);
  } catch {
    return String(r);
  }
};

const imageUrl = (r: unknown): string | null => {
  if (
    r &&
    typeof r === 'object' &&
    typeof (r as { url?: unknown }).url === 'string'
  ) {
    const url = (r as { url: string }).url;
    return url.startsWith('https://') ||
      url.startsWith('http://') ||
      url.startsWith('data:')
      ? url
      : null;
  }
  return null;
};

const BlocksRenderer: React.FC<BlocksRendererProps> = ({ blocks, isError }) => {
  const { t } = useTranslation();
  if (!blocks || blocks.length === 0) return null;

  const textClass = isError
    ? 'mb-1.5 max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-600'
    : 'mb-1.5 max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md border border-gray-200 bg-white p-2 text-[11px] text-gray-700 dark:border-gray-600 dark:bg-[#262626] dark:text-gray-300';

  return (
    <>
      {blocks.map((b, i) => {
        if (b.content_type === 'image') {
          const url = imageUrl(b.result);
          return url ? (
            <img
              key={i}
              src={url}
              alt={t('tool-result-image-alt')}
              className="mb-1.5 max-w-full rounded-md"
            />
          ) : null;
        }
        if (b.content_type === 'json') {
          return (
            <pre key={i} data-testid="json-result-pre" className={textClass}>
              {toText(b.result)}
            </pre>
          );
        }
        // text / markdown / anything else -> raw, as-is
        return (
          <pre key={i} data-testid="tool-result-text" className={textClass}>
            {toText(b.result)}
          </pre>
        );
      })}
    </>
  );
};

export default BlocksRenderer;
